import React, { useCallback, useEffect } from 'react';
import { useAuth } from '../../../../auth/context/AuthProvider';
// The schema for the form itself, likely very similar to UpdateStatusMarkSchema
import { ChangeStatusForm, ChangeStatusSchema } from '../../schema/ChangeStatusSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import FormError from '../../../../shared/components/errors/FormError';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BookingMark } from '../../../../shared/types/booking/BookingMark';
import { BookingStatus } from '../../../../shared/types/booking/BookingStatus';
// --- NEW --- Import the new hook
import { useUpdateBookingStatusMark } from '../../../../services/create-booking-v4/hooks/useUpdateBookingStatusMarkV4';
import { UpdateStatusMarkType } from '../../../../services/create-booking-v4/schema/UpdateStatusMarkSchemaV4';

interface ChangeStatusFormProps {
    reservationId: string;
    onClose: () => void;
}

const ChangeStatusFormComponent: React.FC<ChangeStatusFormProps> = ({ reservationId, onClose }) => {
    const { hasRole, hasPermission } = useAuth();
    const { data: reservation, isFetching, isSuccess, isError } = useShowBooking({
        bookingId: reservationId
    });

    const { control, handleSubmit, reset, setError, formState: { errors } } = useForm<ChangeStatusForm>({
        resolver: zodResolver(ChangeStatusSchema),
    });

    // Effect to populate form with initial data
    useEffect(() => {
        if (isSuccess && reservation) {
            reset({
                status: reservation.data.status,
                mark: reservation.data.mark,
            });
        }
    }, [isSuccess, reservation, reset]);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    // --- NEW --- Use the new hook for updating status and mark
    const { mutate, isPending } = useUpdateBookingStatusMark();

    const queryClient = useQueryClient();

    // --- UPDATED --- The onSubmit function is now much simpler
    const onSubmit = useCallback((values: ChangeStatusForm) => {
        if (!hasRole('dev') && !hasPermission('booking_update')) {
            toast.error("You don't have permission to do this action");
            return;
        }

        // --- FIX --- Add a guard clause to ensure status is not null/undefined.
        // This satisfies TypeScript's strict type checking for the mutation.
        if (!values.status) {
            setError('status', { type: 'manual', message: 'Status is required.' });
            toast.error('Status field cannot be empty.');
            return;
        }

        toast.loading('Changing status...', {
            id: 'change-status'
        });

        // The payload now correctly matches the `UpdateStatusMarkType`.
        const payload: UpdateStatusMarkType = {
            status: values.status as "confirmed" | "unconfirm" | "cancelled" | "modified",
            mark: (["checked_in", "checked_out", "no_show", "early_checkout"].includes(values.mark as string) ? values.mark as "checked_in" | "checked_out" | "no_show" | "early_checkout" : null),
        };

        mutate({ bookingId: reservationId, formData: payload }, {
            onSuccess: async () => {
                handleClose();

                // Refetch queries to update UI across the app
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings', 'availability', 'booking-show'].includes(key as string))
                });

                toast.success('Status changed successfully', { id: 'change-status' });
            },
            onError: (mutationError: any) => {
                // Keep the existing robust error handling
                if (mutationError?.response?.status === 422) {
                    const validationErrors = mutationError.response.data.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof ChangeStatusForm, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                    }
                    toast.error('Please check the form for errors and try again.', { id: 'change-status' });
                } else {
                    toast.error(mutationError.message || 'An unexpected error occurred.', { id: 'change-status' });
                }
            },
        });

    }, [hasRole, hasPermission, reservationId, mutate, handleClose, queryClient, setError]);

    return (
        <>
            {isError && <FormError />}
            {isFetching && <FormSpinner />}
            {isSuccess && reservation && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='form-group mb-3'>
                        <label htmlFor='mark-select'>Mark</label>
                        <Controller
                            name='mark'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    inputId='mark-select'
                                    {...field}
                                    options={BookingMark.map(status => ({ value: status.value, label: status.label }))}
                                    onChange={option => field.onChange(option ? option.value : null)}
                                    value={BookingMark.find(status => status.value === field.value) || null}
                                    isClearable
                                />
                            )}
                        />
                        {errors.mark && <div className='text-danger mt-1'>{errors.mark.message}</div>}
                    </div>

                    <div className='form-group mb-3'>
                        <label htmlFor='status-select'>Status</label>
                        <Controller
                            name='status'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    inputId='status-select'
                                    {...field}
                                    options={BookingStatus.map(status => ({ value: status.value, label: status.label }))}
                                    onChange={option => field.onChange(option ? option.value : null)}
                                    value={BookingStatus.find(status => status.value === field.value) || null}
                                />
                            )}
                        />
                        {errors.status && <div className='text-danger mt-1'>{errors.status.message}</div>}
                    </div>
                    <Button className='mt-3' type='submit' disabled={isPending}>
                        {isPending ? 'Submitting...' : 'Submit'}
                    </Button>
                </form>
            )}
        </>
    );
};

export default ChangeStatusFormComponent;
